"use client";

import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { getGPUTier } from "detect-gpu";
import { usePageVisibility } from "@/hooks/usePageVisibility";

type MotionMode = "on" | "off";

interface MotionContextType {
  mode: MotionMode;
  mounted: boolean;
  gpuTier: number;
  reducedMotion: boolean;
  canAnimate: boolean;
  /**
   * True when the tab is hidden (document.visibilityState !== "visible").
   * Intended for pausing continuous-rAF work in place WITHOUT tearing
   * down GSAP contexts or unmounting DOM. Orthogonal to canAnimate.
   */
  backgrounded: boolean;
  /**
   * Convenience for continuous-rAF consumers: true only when the user is
   * permitted to see motion AND the tab is currently visible. Do NOT use
   * this to gate setup/teardown effects — use canAnimate for that.
   */
  shouldRender: boolean;
  setMode: (mode: MotionMode) => void;
  toggleMode: () => void;
  setGpuTier: (tier: number) => void;
}

const MotionContext = createContext<MotionContextType | null>(null);

export function MotionProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<MotionMode>("on");
  const [mounted, setMounted] = useState(false);
  const [gpuTier, setGpuTier] = useState(2); // Default to mid-tier
  const [reducedMotion, setReducedMotion] = useState(false);
  const isVisible = usePageVisibility();

  // Derived: canAnimate = !reducedMotion && gpuTier >= 1 && mode === "on"
  // Note: Safari often misdetects as tier 1 despite capable GPU, so we allow tier 1
  const canAnimate = mounted && !reducedMotion && gpuTier >= 1 && mode === "on";

  // Derived: backgrounded freezes continuous work without tearing anything down.
  // Gated on `mounted` to avoid an SSR mismatch: before hydration we never
  // claim to be backgrounded.
  const backgrounded = mounted && !isVisible;
  const shouldRender = canAnimate && !backgrounded;

  // Detect GPU tier and reduced motion preference in parallel
  useEffect(() => {
    let isMounted = true;

    const initMotion = async () => {
      // Check prefers-reduced-motion
      const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
      const prefersReduced = mediaQuery.matches;

      if (isMounted) {
        setReducedMotion(prefersReduced);
      }

      // Listen for changes to reduced motion preference
      const handleReducedMotionChange = (e: MediaQueryListEvent) => {
        if (isMounted) {
          setReducedMotion(e.matches);
          if (e.matches) {
            setMode("off");
          }
        }
      };
      mediaQuery.addEventListener("change", handleReducedMotionChange);

      // If user prefers reduced motion, set mode off immediately
      if (prefersReduced) {
        if (isMounted) {
          setMode("off");
          setMounted(true);
        }
        return () => mediaQuery.removeEventListener("change", handleReducedMotionChange);
      }

      // Detect GPU tier in parallel
      try {
        const result = await getGPUTier();
        if (!isMounted) return;

        setGpuTier(result.tier);

        // Log in development
        if (process.env.NODE_ENV === "development") {
          console.log("[Performance]", {
            gpuTier: result.tier,
            reducedMotion: prefersReduced,
            canAnimate: !prefersReduced && result.tier >= 1,
            isMobile: result.isMobile,
            gpu: result.gpu,
          });
        }

        // Tier 0: Fallback/unsupported GPU, disable animations
        // Note: We allow tier 1 because Safari often misdetects capable GPUs as tier 1
        if (result.tier === 0) {
          setMode("off");
          if (process.env.NODE_ENV === "development") {
            console.log("[Motion] Disabled for unsupported GPU (tier", result.tier, ")");
          }
        } else {
          setMode("on");
        }
      } catch (error) {
        // On detection failure, default to motion on
        if (process.env.NODE_ENV === "development") {
          console.warn("[GPU Tier] Detection failed:", error);
        }
        setMode("on");
      }

      if (isMounted) {
        setMounted(true);
      }

      return () => mediaQuery.removeEventListener("change", handleReducedMotionChange);
    };

    const cleanup = initMotion();

    return () => {
      isMounted = false;
      cleanup?.then(fn => fn?.());
    };
  }, []);

  const toggleMode = () => {
    setMode((prev) => (prev === "on" ? "off" : "on"));
  };

  // Don't block rendering - motion state can update after mount
  return (
    <MotionContext.Provider value={{ mode, mounted, gpuTier, reducedMotion, canAnimate, backgrounded, shouldRender, setMode, toggleMode, setGpuTier }}>
      {children}
    </MotionContext.Provider>
  );
}

export function useMotionMode() {
  const context = useContext(MotionContext);
  // Return default values for SSR/static generation
  if (!context) {
    return {
      mode: "on" as MotionMode,
      mounted: false,
      gpuTier: 2,
      reducedMotion: false,
      canAnimate: false, // Default to false until mounted
      backgrounded: false,
      shouldRender: false,
      setMode: () => {},
      toggleMode: () => {},
      setGpuTier: () => {},
    };
  }
  return context;
}
